import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import VocabCard from "../components/VocabCard";
import { vocabularyTerms, categories } from "../data/vocabulary";
import useScrollAnimation from "../hooks/useScrollAnimation";

export default function Vocabulary() {
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");

  useScrollAnimation(".scroll-reveal");

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return vocabularyTerms.filter((t) => {
      const matchesCat = category === "all" || t.category === category;
      const matchesText =
        !q ||
        t.term.toLowerCase().includes(q) ||
        t.shortDef.toLowerCase().includes(q) ||
        t.definition.toLowerCase().includes(q);
      return matchesCat && matchesText;
    });
  }, [search, category]);

  return (
    <section className="vocab-page section">
      <div className="container">
        {/* Header */}
        <div className="section-header vocab-page__header scroll-reveal">
          <h2>AI Glossary</h2>
          <span className="accent-line" />
          <p>
            {vocabularyTerms.length} terms — from gradient descent to frontier
            models and the labs building them. Click any card to expand the full
            definition.
          </p>
        </div>

        {/* Search + Category filter */}
        <div className="vocab-page__controls scroll-reveal">
          <input
            className="input vocab-page__search"
            type="search"
            placeholder="Search terms…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <div className="vocab-page__cats">
            <button
              className={`btn btn-ghost vocab-cat ${category === "all" ? "vocab-cat--active" : ""}`}
              onClick={() => setCategory("all")}
            >
              All ({vocabularyTerms.length})
            </button>
            {categories.map((c) => (
              <button
                key={c.id}
                className={`btn btn-ghost vocab-cat ${category === c.id ? "vocab-cat--active" : ""}`}
                onClick={() => setCategory(c.id)}
                style={{ "--cat": c.color }}
              >
                {c.label} (
                {vocabularyTerms.filter((t) => t.category === c.id).length})
              </button>
            ))}
          </div>
        </div>

        {/* Results count */}
        {search && (
          <p className="vocab-page__count text-muted">
            {filtered.length} result{filtered.length !== 1 ? "s" : ""} for "
            {search}"
          </p>
        )}

        {/* Grid */}
        {filtered.length > 0 ? (
          <motion.div className="vocab-grid" layout>
            {filtered.map((t) => (
              <VocabCard key={t.id} term={t} />
            ))}
          </motion.div>
        ) : (
          <p className="vocab-page__empty text-muted">
            No terms match your search.
          </p>
        )}
      </div>

      <style>{`
        .vocab-page__header { margin-bottom: var(--space-xl); }

        .vocab-page__controls {
          display: flex;
          flex-direction: column;
          gap: var(--space-md);
          margin-bottom: var(--space-lg);
        }
        .vocab-page__search { max-width: 380px; }

        .vocab-page__cats {
          display: flex;
          flex-wrap: wrap;
          gap: var(--space-xs);
        }
        .vocab-cat {
          font-size: 0.78rem;
          padding: 0.3rem 0.8rem;
          color: var(--text-muted);
        }
        .vocab-cat--active {
          background: var(--accent-soft);
          color: var(--accent);
          border: 1px solid var(--border);
        }
        /* Use per-category color when active */
        .vocab-cat--active[style] {
          background: color-mix(in srgb, var(--cat) 12%, transparent);
          color: var(--cat);
          border-color: color-mix(in srgb, var(--cat) 25%, transparent);
        }

        .vocab-page__count {
          font-size: 0.82rem;
          margin-bottom: var(--space-md);
        }

        .vocab-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
          gap: var(--space-md);
        }

        .vocab-page__empty {
          text-align: center;
          padding: var(--space-3xl) 0;
          font-size: 0.95rem;
        }

        .scroll-reveal {
          opacity: 0;
          transform: translateY(16px);
          transition: opacity 0.5s ease, transform 0.5s ease;
        }
        .scroll-reveal.visible { opacity: 1; transform: none; }

        @media (max-width: 600px) {
          .vocab-grid { grid-template-columns: 1fr; }
        }
      `}</style>
    </section>
  );
}
